import type { ChainId_L1 } from '@cronos-app/sdk';
import {
  CRO_DECIMALS,
  CRO_DECIMALS_ON_ETHEREUM,
  LCRO_DECIMALS,
  ZKCRO_DECIMALS,
} from '@cronos-app/sdk';
import { currentWallet } from '@cronos-app/wallet';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { Token } from '@ui/models';
import { getSDKs } from '@ui/utils';
import { Fraction } from 'bi-fraction';

type UseZkcroPerTokenOptions = Partial<UseQueryOptions<Fraction>> & {
  token: Token;
};

/**
 * useZkcroPerToken() = zkCroAmount / tokenAmount
 */
export const useZkcroPerToken = ({
  token,
  ...options
}: UseZkcroPerTokenOptions) => {
  const desiredNetwork = currentWallet.useDesiredNetwork();

  return useQuery<Fraction>({
    queryKey: ['useZkcroPerToken', desiredNetwork, token],
    queryFn: () => getZkcroPerToken(desiredNetwork, token),
    ...options,
  });
};

export const getZkcroPerToken = async (
  desiredNetwork: ChainId_L1,
  token: Token,
) => {
  const sdks = getSDKs(desiredNetwork);

  if (token === Token.CRO || token === Token.LCRO) {
    const oneCro = Fraction.ONE;

    const zkCroPerCro = new Fraction(
      await sdks.L1.getZkCroContract().convertToShare(
        oneCro.shl(CRO_DECIMALS_ON_ETHEREUM).quotient,
      ),
    ).shr(ZKCRO_DECIMALS);

    // if fromToken is cro
    if (token === Token.CRO) return zkCroPerCro;

    // if fromToken is Lcro
    const oneLcro = Fraction.ONE;

    const croPerLcro = new Fraction(
      await sdks.CronosEvm.getLiquidCroContract().convertToAsset(
        oneLcro.shl(LCRO_DECIMALS).quotient,
      ),
    ).shr(CRO_DECIMALS);

    return zkCroPerCro.mul(croPerLcro);
  }

  return Fraction.ONE;
};
